import React, { useState } from 'react';
import { Button, Card, Table, Spinner, Alert, Modal, Row, Col } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrashAlt, FaTicketAlt, FaStar, FaEuroSign, FaExclamationCircle, FaPalette } from 'react-icons/fa';
import { useTicketTypes } from '../../../hooks/TICKET/useTicketTypes';
import { useDeleteTicketType } from '../../../hooks/TICKET/useDeleteTicketType';
import { useFacturationModes } from '../../../hooks/TICKET/useFacturationModes';
import { useDeleteFacturationMode } from '../../../hooks/TICKET/useDeleteFacturationMode';
import { useTicketPriorities } from '../../../hooks/TICKET/useTicketPriorities';
import { useDeleteTicketPriority } from '../../../hooks/TICKET/useDeleteTicketPriority';
import { useColors } from '../../../hooks/COLORS/useColors';
import { useDeleteColor } from '../../../hooks/COLORS/useDeleteColor';
import NewTypeTicket from '../../Modals/Create/NewTypeTicket';
import UpdateTypeTicket from '../../Modals/Update/UpdateTypeTicket';
import NewFacturationMode from '../../Modals/Create/NewFacturationMode';
import UpdateFacturationMode from '../../Modals/Update/UpdateFacturationMode';
import NewPriority from '../../Modals/Create/NewPriority';
import UpdatePriority from '../../Modals/Update/UpdatePriority';
import NewColor from '../../Modals/Create/NewColor';
import UpdateColor from '../../Modals/Update/UpdateColor';

const ParametresTab = () => {
    // États pour les types de tickets
    const [showNewTypeTicket, setShowNewTypeTicket] = useState(false);
    const [showUpdateTypeTicket, setShowUpdateTypeTicket] = useState(false);
    const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [typeToDelete, setTypeToDelete] = useState(null);

    // États pour les méthodes de facturation
    const [showNewFacturationMode, setShowNewFacturationMode] = useState(false);
    const [showUpdateFacturationMode, setShowUpdateFacturationMode] = useState(false);
    const [showDeleteModeModal, setShowDeleteModeModal] = useState(false);
    const [selectedMode, setSelectedMode] = useState(null);
    const [modeToDelete, setModeToDelete] = useState(null);

    // États pour les priorités
    const [showNewPriority, setShowNewPriority] = useState(false);
    const [showUpdatePriority, setShowUpdatePriority] = useState(false);
    const [showDeletePriorityModal, setShowDeletePriorityModal] = useState(false);
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [priorityToDelete, setPriorityToDelete] = useState(null);

    // États pour les couleurs
    const [showNewColor, setShowNewColor] = useState(false);
    const [showUpdateColor, setShowUpdateColor] = useState(false);
    const [selectedColor, setSelectedColor] = useState(null);
    const [showDeleteColorModal, setShowDeleteColorModal] = useState(false);
    const [colorToDelete, setColorToDelete] = useState(null);

    // Queries
    const { data: typesData, isLoading: isLoadingTypes, isError: isErrorTypes } = useTicketTypes();
    const { data: modesData, isLoading: isLoadingModes, isError: isErrorModes } = useFacturationModes();
    const { data: prioritiesData, isLoading: isLoadingPriorities, isError: isErrorPriorities } = useTicketPriorities();
    const { data: colorsData, isLoading: isLoadingColors, isError: isErrorColors } = useColors();

    // Mutations
    const { mutate: deleteType, isLoading: isDeletingType } = useDeleteTicketType();
    const { mutate: deleteMode, isLoading: isDeletingMode } = useDeleteFacturationMode();
    const { mutate: deletePriority, isLoading: isDeletingPriority } = useDeleteTicketPriority();
    const { mutate: deleteColor, isLoading: isDeletingColor } = useDeleteColor();

    // Données filtrées
    const types = typesData?.data?.filter(type => type.is_active) || [];
    const modes = modesData?.data?.filter(mode => mode.is_active) || [];
    const priorities = prioritiesData?.data?.filter(priority => priority.is_active) || [];
    const colors = colorsData?.data?.filter(color => color.is_active) || [];

    // Handlers pour les types de tickets
    const handleEditType = (type) => {
        setSelectedType(type);
        setShowUpdateTypeTicket(true);
    };

    const handleDeleteTypeClick = (type) => {
        setTypeToDelete(type);
        setShowDeleteTypeModal(true);
    };

    const confirmDeleteType = () => {
        if (typeToDelete) {
            deleteType(typeToDelete.id, {
                onSuccess: () => {
                    setShowDeleteTypeModal(false);
                    setTypeToDelete(null);
                }
            });
        }
    };

    // Handlers pour les méthodes de facturation
    const handleEditMode = (mode) => {
        setSelectedMode(mode);
        setShowUpdateFacturationMode(true);
    };

    const handleDeleteModeClick = (mode) => {
        setModeToDelete(mode);
        setShowDeleteModeModal(true);
    };

    const confirmDeleteMode = () => {
        if (modeToDelete) {
            deleteMode(modeToDelete.id, {
                onSuccess: () => {
                    setShowDeleteModeModal(false);
                    setModeToDelete(null);
                }
            });
        }
    };

    // Handlers pour les priorités
    const handleEditPriority = (priority) => {
        setSelectedPriority(priority);
        setShowUpdatePriority(true);
    };

    const handleDeletePriorityClick = (priority) => {
        setPriorityToDelete(priority);
        setShowDeletePriorityModal(true);
    };

    const confirmDeletePriority = () => {
        if (priorityToDelete) {
            deletePriority(priorityToDelete.id, {
                onSuccess: () => {
                    setShowDeletePriorityModal(false);
                    setPriorityToDelete(null);
                }
            });
        }
    };

    // Handlers pour les couleurs (désactivés)
    const handleEditColor = (color) => {
    setSelectedColor(color);
    setShowUpdateColor(true);
};

const handleDeleteColorClick = (color) => {
    setColorToDelete(color);
    setShowDeleteColorModal(true);
};

const confirmDeleteColor = () => {
    if (colorToDelete) {
        deleteColor(colorToDelete.id, {
            onSuccess: () => {
                setShowDeleteColorModal(false);
                setColorToDelete(null);
            }
        });
    }
};

    if (isLoadingTypes || isLoadingModes || isLoadingPriorities || isLoadingColors) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" style={{ color: "#4e73df" }}>
                    <span className="visually-hidden">Chargement...</span>
                </Spinner>
            </div>
        );
    }

    if (isErrorTypes || isErrorModes || isErrorPriorities || isErrorColors) {
        return (
            <Alert variant="danger">
                <p className="mb-2"><strong>Erreur lors du chargement des paramètres :</strong></p>
                <ul className="mb-0">
                    {isErrorTypes && <li>Impossible de charger les types de tickets</li>}
                    {isErrorModes && <li>Impossible de charger les méthodes de facturation</li>}
                    {isErrorPriorities && <li>Impossible de charger les priorités</li>}
                    {isErrorColors && <li>Impossible de charger les couleurs</li>}
                </ul>
            </Alert>
        );
    }

    return (
        <>
            {/* Première ligne : Méthodes de facturation et Priorités */}
            <Row className="mb-4">
                {/* Méthodes de facturation */}
                <Col md={6}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0"><FaEuroSign className="me-2" />Méthodes de facturation</h5>
                        <Button variant="primary" className="btn-icon-split" onClick={() => setShowNewFacturationMode(true)}>
                            <span className="icon text-white">
                                <FaPlus />
                            </span>
                            <span className="text">Ajouter</span>
                        </Button>
                    </div>

                    <Card className="shadow">
                        <Card.Header className="py-3">
                            <h6 className="m-0 fw-bold text-success">Liste des méthodes de facturation</h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Label</th>
                                            <th>Couleur</th>
                                            <th className="text-center">Défaut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modes.length > 0 ? (
                                            modes.map((mode) => (
                                                <tr key={mode.id}>
                                                    <td>{mode.id}</td>
                                                    <td>{mode.label}</td>
                                                    <td>
                                                        {mode.color ? (
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div 
                                                                    style={{
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: mode.color?.hex || '#cccccc',
                                                                        border: '1px solid #dee2e6'
                                                                    }}
                                                                    title={mode.color?.name}
                                                                ></div>
                                                                <small className="text-muted">{mode.color?.name}</small>
                                                            </div>
                                                        ) : (
                                                            <small className="text-muted">-</small>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {mode.is_default && <FaStar className="text-warning" />}
                                                    </td>
                                                    <td className="text-center">
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            className="me-2 p-1" 
                                                            title="Modifier"
                                                            onClick={() => handleEditMode(mode)}
                                                        >
                                                            <FaEdit className="text-warning" />
                                                        </Button>
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            className="p-1" 
                                                            title="Supprimer"
                                                            onClick={() => handleDeleteModeClick(mode)}
                                                        >
                                                            <FaTrashAlt className="text-danger" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted">
                                                    Aucune méthode de facturation trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Priorités */}
                <Col md={6}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0"><FaExclamationCircle className="me-2" />Priorités</h5>
                        <Button variant="primary" className="btn-icon-split" onClick={() => setShowNewPriority(true)}>
                            <span className="icon text-white">
                                <FaPlus />
                            </span>
                            <span className="text">Ajouter</span>
                        </Button>
                    </div>

                    <Card className="shadow">
                        <Card.Header className="py-3">
                            <h6 className="m-0 fw-bold text-danger">Liste des priorités</h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Nom</th>
                                            <th>Label</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {priorities.length > 0 ? (
                                            priorities.map((priority) => (
                                                <tr key={priority.id}>
                                                    <td>{priority.id}</td>
                                                    <td>{priority.name}</td>
                                                    <td><small className="text-muted">{priority.label || '-'}</small></td>
                                                    <td className="text-center">
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            className="me-2 p-1" 
                                                            title="Modifier"
                                                            onClick={() => handleEditPriority(priority)}
                                                        >
                                                            <FaEdit className="text-warning" />
                                                        </Button>
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            className="p-1" 
                                                            title="Supprimer"
                                                            onClick={() => handleDeletePriorityClick(priority)}
                                                        >
                                                            <FaTrashAlt className="text-danger" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center text-muted">
                                                    Aucune priorité trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Deuxième ligne : Types de tickets */}
            <Row>
                <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0"><FaTicketAlt className="me-2" />Types de tickets</h5>
                        <Button variant="primary" className="btn-icon-split" onClick={() => setShowNewTypeTicket(true)}>
                            <span className="icon text-white">
                                <FaPlus />
                            </span>
                            <span className="text">Ajouter</span>
                        </Button>
                    </div>

                    <Card className="shadow">
                    <Card.Header className="py-3">
                        <h6 className="m-0 fw-bold text-ticket">Liste des types de tickets</h6>
                    </Card.Header>
                    <Card.Body>
                        <div className="table-responsive">
                            <Table hover>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Label</th>
                                        <th>Nom</th>
                                        <th>Couleur</th>
                                        <th className="text-center">Défaut</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {types.length > 0 ? (
                                        types.map((type) => (
                                            <tr key={type.id}>
                                                <td>{type.id}</td>
                                                <td>{type.label}</td>
                                                <td><small className="text-muted">{type.name}</small></td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div 
                                                            style={{
                                                                width: '20px',
                                                                height: '20px',
                                                                borderRadius: '50%',
                                                                backgroundColor: type.color?.hex || '#cccccc',
                                                                border: '1px solid #dee2e6'
                                                            }}
                                                            title={type.color?.name}
                                                        ></div>
                                                        <small className="text-muted">{type.color?.name}</small>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {type.is_default && <FaStar className="text-warning" />}
                                                </td>
                                                <td className="text-center">
                                                    <Button 
                                                        variant="link" 
                                                        size="sm" 
                                                        className="me-2 p-1" 
                                                        title="Modifier"
                                                        onClick={() => handleEditType(type)}
                                                    >
                                                        <FaEdit className="text-warning" />
                                                    </Button>
                                                    <Button 
                                                        variant="link" 
                                                        size="sm" 
                                                        className="p-1" 
                                                        title="Supprimer"
                                                        onClick={() => handleDeleteTypeClick(type)}
                                                    >
                                                        <FaTrashAlt className="text-danger" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted">
                                                Aucun type de ticket trouvé
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
                </Col>
            </Row>

            {/* Troisième ligne : Couleurs */}
            <Row className="mt-4">
                <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0"><FaPalette className="me-2" />Couleurs</h5>
                        <Button variant="primary" className="btn-icon-split" onClick={() => setShowNewColor(true)}>
                            <span className="icon text-white">
                                <FaPlus />
                            </span>
                            <span className="text">Ajouter une couleur</span>
                        </Button>
                    </div>

                    <Card className="shadow">
                        <Card.Header className="py-3">
                            <h6 className="m-0 fw-bold text-primary">Liste des couleurs</h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Nom</th>
                                            <th>Code HEX</th>
                                            <th>Aperçu</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {colors.length > 0 ? (
                                            colors.map((color) => (
                                                <tr key={color.id}>
                                                    <td>{color.id}</td>
                                                    <td>{color.name}</td>
                                                    <td><code>{color.hex}</code></td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div 
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '6px',
                                                                    backgroundColor: color.hex,
                                                                    border: '2px solid #dee2e6',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                }}
                                                                title={color.name}
                                                            ></div>
                                                            <span className="text-muted small">{color.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="me-2 p-1"
                                                        title="Modifier"
                                                        onClick={() => handleEditColor(color)}
                                                    >
                                                        <FaEdit className="text-warning" />
                                                    </Button>
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="p-1"
                                                        title="Supprimer"
                                                        onClick={() => handleDeleteColorClick(color)}
                                                    >
                                                        <FaTrashAlt className="text-danger" />
                                                    </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted">
                                                    Aucune couleur trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modales types de tickets */}
            <NewTypeTicket 
                showNewTypeTicket={showNewTypeTicket} 
                setShowNewTypeTicket={setShowNewTypeTicket} 
            />
            <UpdateTypeTicket 
                showUpdateTypeTicket={showUpdateTypeTicket} 
                setShowUpdateTypeTicket={setShowUpdateTypeTicket}
                type={selectedType}
            />

            {/* Modales méthodes de facturation */}
            <NewFacturationMode 
                showNewFacturationMode={showNewFacturationMode} 
                setShowNewFacturationMode={setShowNewFacturationMode} 
            />
            <UpdateFacturationMode 
                showUpdateFacturationMode={showUpdateFacturationMode} 
                setShowUpdateFacturationMode={setShowUpdateFacturationMode}
                mode={selectedMode}
            />

            {/* Modales priorités */}
            <NewPriority 
                showNewPriority={showNewPriority} 
                setShowNewPriority={setShowNewPriority} 
            />
            <UpdatePriority 
                showUpdatePriority={showUpdatePriority} 
                setShowUpdatePriority={setShowUpdatePriority}
                priority={selectedPriority}
            />

            {/* Modales couleurs */}
            <NewColor 
                showNewColor={showNewColor} 
                setShowNewColor={setShowNewColor} 
            />
            <UpdateColor 
                showUpdateColor={showUpdateColor} 
                setShowUpdateColor={setShowUpdateColor}
                color={selectedColor}
            />

            {/* Modale de confirmation de suppression type */}
            <Modal show={showDeleteTypeModal} onHide={() => setShowDeleteTypeModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer le type de ticket <strong>{typeToDelete?.label}</strong> ?</p>
                    <p className="text-muted small">Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteTypeModal(false)} disabled={isDeletingType}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDeleteType} disabled={isDeletingType}>
                        {isDeletingType ? 'Suppression...' : 'Supprimer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modale de confirmation de suppression méthode */}
            <Modal show={showDeleteModeModal} onHide={() => setShowDeleteModeModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer la méthode de facturation <strong>{modeToDelete?.label}</strong> ?</p>
                    <p className="text-muted small">Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModeModal(false)} disabled={isDeletingMode}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDeleteMode} disabled={isDeletingMode}>
                        {isDeletingMode ? 'Suppression...' : 'Supprimer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modale de confirmation de suppression priorité */}
            <Modal show={showDeletePriorityModal} onHide={() => setShowDeletePriorityModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer la priorité <strong>{priorityToDelete?.name}</strong> ?</p>
                    <p className="text-muted small">Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeletePriorityModal(false)} disabled={isDeletingPriority}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDeletePriority} disabled={isDeletingPriority}>
                        {isDeletingPriority ? 'Suppression...' : 'Supprimer'}
                    </Button>
                </Modal.Footer>
            </Modal>
                    <Modal show={showDeleteColorModal} onHide={() => setShowDeleteColorModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>Confirmer la suppression</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Êtes-vous sûr de vouloir supprimer la couleur <strong>{colorToDelete?.name}</strong> ?</p>
                <p className="text-muted small">Cette action est irréversible.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteColorModal(false)} disabled={isDeletingColor}>
                    Annuler
                </Button>
                <Button variant="danger" onClick={confirmDeleteColor} disabled={isDeletingColor}>
                    {isDeletingColor ? 'Suppression...' : 'Supprimer'}
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default ParametresTab;

